import { jewelModeNotice, type OwnedJewel, type SkinSockets } from '@kenocyber/shared';
import { jewelLook } from './jewel-appearance';

/**
 * Os assentos de UMA cópia de skin, no vestiário (gate V28C).
 *
 * ## A identidade de um assento é persistida
 *
 * Cada botão envia `(skinInstanceId, socketIndex)` - o índice que veio do
 * servidor, e nunca a posição do elemento na lista. Reordenar, filtrar ou
 * esconder assentos no ecrã não muda o que uma ação diz.
 *
 * ## Duas cópias da mesma skin não partilham nada
 *
 * Este componente recebe os assentos de UMA cópia. Duas cópias são dois
 * componentes com dois `skinInstanceId` diferentes, e é por isso que encaixar
 * numa não mexe na outra.
 *
 * ## Capacidade desconhecida não é zero
 *
 * Quando `sockets` é nulo, não se desenha fila nenhuma: diz-se por palavras que
 * a capacidade não está registada. Desenhar zero assentos afirmaria que a cópia
 * não tem nenhum, que é uma coisa que ninguém sabe.
 */

export interface JewelSocketRowProps {
  readonly skin: SkinSockets;
  /** Todas as joias da conta, para saber o que está em cada assento. */
  readonly jewels: readonly OwnedJewel[];
  readonly assetBaseUrl: string;
  readonly busy?: boolean;
  readonly onInsert: (socketIndex: number) => void;
  readonly onRemove: (socketIndex: number) => void;
  readonly onSwap: (socketIndex: number) => void;
}

export function JewelSocketRow({
  skin,
  jewels,
  assetBaseUrl,
  busy = false,
  onInsert,
  onRemove,
  onSwap,
}: JewelSocketRowProps) {
  if (skin.sockets === null) {
    return (
      <p
        className="jewel-capacity-unknown"
        data-testid={`jewel-sockets-unknown-${skin.skinInstanceId}`}
      >
        Capacidade não registrada
      </p>
    );
  }

  const byInstance = new Map(jewels.map((jewel) => [jewel.instanceId, jewel]));

  return (
    <div className="jewel-socket-row" data-testid={`jewel-sockets-${skin.skinInstanceId}`}>
      {skin.sockets.map((seat) => {
        const jewel = seat.jewelInstanceId ? byInstance.get(seat.jewelInstanceId) : undefined;
        const look = jewel ? jewelLook(jewel.definitionId) : null;
        return (
          <div
            key={seat.index}
            className="jewel-socket-seat"
            data-testid={`jewel-seat-${skin.skinInstanceId}-${seat.index}`}
            data-filled={jewel ? 'yes' : 'no'}
            style={
              look
                ? ({ '--jewel-primary': look.palette.primary } as React.CSSProperties)
                : undefined
            }
          >
            {/* O número que o jogador lê começa em 1; o que a ação envia é o
                índice real do servidor. */}
            <span className="jewel-socket-index">espaço {seat.index + 1}</span>
            {jewel ? (
              <>
                <img
                  src={`${assetBaseUrl}${jewel.assetFile}`}
                  alt={jewel.displayName}
                  width={44}
                  height={44}
                  draggable={false}
                />
                <span className="jewel-socket-name">{jewel.displayName}</span>
                <span className="jewel-socket-index">Nível {jewel.level}</span>
                {/*
                 * **O aviso de MODO, também aqui** (gate V28G.O.1).
                 *
                 * Não é repetição: a carta vive no separador das joias e este
                 * assento no das skins, e os dois nunca estão no ecrã ao mesmo
                 * tempo. Este é o sítio onde o jogador VÊ o que está encaixado e
                 * decide substituir - e é onde a pergunta "isto ajuda-me a
                 * caçar?" aparece.
                 *
                 * Texto, e não uma cor: o assento já pinta uma variável com a
                 * paleta da peça, e quem não distingue cores continua a ler o
                 * mesmo que toda a gente.
                 */}
                {(() => {
                  const notice = jewelModeNotice(jewel.definitionId);
                  return notice === null ? null : (
                    <span
                      className="jewel-socket-mode"
                      data-testid={`jewel-seat-mode-${skin.skinInstanceId}-${seat.index}`}
                      role="note"
                      aria-label={`${notice.badge}. ${notice.note}`}
                    >
                      {notice.badge}
                    </span>
                  );
                })()}
                <span className="jewel-socket-actions">
                  <button
                    type="button"
                    data-testid={`jewel-remove-${skin.skinInstanceId}-${seat.index}`}
                    onClick={() => onRemove(seat.index)}
                    disabled={busy}
                  >
                    Retirar
                  </button>
                  <button
                    type="button"
                    data-testid={`jewel-swap-${skin.skinInstanceId}-${seat.index}`}
                    onClick={() => onSwap(seat.index)}
                    disabled={busy}
                  >
                    Substituir
                  </button>
                </span>
              </>
            ) : (
              <button
                type="button"
                data-testid={`jewel-insert-${skin.skinInstanceId}-${seat.index}`}
                onClick={() => onInsert(seat.index)}
                disabled={busy}
              >
                Encaixar
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
